import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';

import { Button, Grid } from '@material-ui/core';
import { Visibility } from '@material-ui/icons';

import { FetchError } from '../../common/api/api';
import AppSnackbar from '../../common/AppSnackbar';
import HoveredCard from '../../common/card/HoveredCard';
import DeleteEntityCard from '../../common/cards/DeleteEntityCard';
import CartIconBadge from '../../common/cart/CartIconBadge';
import { formatDate } from '../../common/formatter';
import HeaderBackButton, {
  StyledPageTemplate
} from '../../common/HeaderBackButton';
import HeaderTitle from '../../common/HeaderTitle';
import PageTemplateAlert from '../../common/page-template/PageTemplateAlert';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import TaskStatusLabel from '../../common/TaskStatusLabel';
import environment from '../../environments';
import { useUserContext } from '../../user/User.context';
import { useJiraConnectAPI } from '../jira-connect/JiraConnect.hooks';
import { JiraServerContext } from '../jira-server/jira-server.context';
import SubTaskListCard from '../tasks/cards/SubTaskListCard';
import TaskAssignCard from '../tasks/cards/TaskAssignCard';
import TaskAttachmentCard from '../tasks/cards/TaskAttachmentCard';
import TaskFileListCard from '../tasks/cards/TaskFileListCard';
import TaskStatusVersionCard from '../tasks/cards/TaskStatusVersionCard';
import { Task } from '../tasks/Task.interface';
import TaskStatusActions from '../tasks/TaskStatusActions';
import { useTaskEntityFields } from '../workflow/Workflow.hooks';
import { ProductBoardColumnEntity } from './board/ProductBoardColumn';
import TaskJiraIssueAssignCard from '../tasks/cards/TaskJiraIssueAssignCard';
import ProductsSubSectionComponentCard from './cards/ProductsSubSectionComponentCard';
import ProductsSubSectionReleaseCard from './cards/ProductsSubSectionReleaseCard';
import TestStepCard from './cards/TestStepCard';
import VerificationDesignInputReferenceCard from './cards/VerificationDesignInputReferenceCard';
import VerificationDesignOutputReferenceCard from './cards/VerificationDesignOutputReferenceCard';
import VerificationInfoCard from './cards/VerificationInfoCard';
import VerificationUserNeedReferenceCard from './cards/VerificationUserNeedReferenceCard';
import { getProductEntityName } from './Product.config';
import {
  useFindProductVerificationById,
  useFindProductVerificationVersion,
  useProductEntityPrefixId
} from './Product.hooks';
import {
  BaseProductVerificationCardProps,
  ProductVerification
} from './Product.interface';
import TaskDueDateCard from '../tasks/cards/TaskDueDateCard';

const ProductVerificationDetailPage: React.FC = () => {
  const {
    productId: paramProductId = '',
    verificationId: paramVerificationId = ''
  } = useParams<{ productId: string; verificationId: string }>();
  const productId: number = parseInt(paramProductId) || 0;
  const verificationId: number = parseInt(paramVerificationId) || 0;
  const fetchUserNeedsRef = useRef<Function | undefined>();
  const fetchDesignInputsRef = useRef<Function | undefined>();
  const { hasRole } = useUserContext();
  const [from = 'detail'] = useQueryParam('from', StringParam);
  const [version] = useQueryParam('ver', withDefault(NumberParam, undefined));
  const [productVerification, setProductVerification] = useState<
    ProductVerification | undefined
  >();
  const [didUpdate, setDidUpdate] = useState<boolean>(false);
  const [error, setError] = useState<FetchError | undefined>();
  const { isAvailable: isJiraConnect } = useJiraConnectAPI();
  const { isJiraServer } = useContext(JiraServerContext);
  const {
    productVerification: data,
    isLoadingProductVerification,
    findProductVerificationError,
    findProductVerification
  } = useFindProductVerificationById(productId, verificationId);
  const {
    productVerificationVersion: loadedProductVerificationVersion,
    findProductVerificationVersion
  } = useFindProductVerificationVersion(productId, verificationId, version);
  const [productVerificationVersion, setProductVerificationVersion] = useState(
    loadedProductVerificationVersion
  );

  const title: string = useProductEntityPrefixId(
    productVerification,
    ProductBoardColumnEntity.Verification
  );
  const { isFieldDisabled } = useTaskEntityFields(productVerification?.task);

  const cardProps: BaseProductVerificationCardProps = {
    productId,
    productVerification: productVerification!,
    productVerificationVersion,
    onChange: (newProductVerification: Partial<ProductVerification>) => {
      if (productVerification) {
        // notify task components to refresh
        productVerification.task = { ...productVerification.task };
        setProductVerification({
          ...productVerification,
          ...newProductVerification
        });
      }
    },
    onUpdate: () => setDidUpdate(true),
    onError: (error: FetchError) => setError(error)
  };

  const handleTaskUpdate = (newTask: Partial<Task>) => {
    if (!productVerification?.task) {
      return;
    }
    Object.assign(productVerification.task, newTask);
    cardProps.onChange?.({});
    cardProps.onUpdate?.();
  };

  useEffect(() => {
    findProductVerification();
  }, [findProductVerification, productId, verificationId]);
  useEffect(() => {
    if (version) {
      findProductVerificationVersion();
    } else {
      setProductVerificationVersion(undefined);
    }
  }, [findProductVerificationVersion, productId, verificationId, version]);
  useEffect(
    () =>
      loadedProductVerificationVersion &&
      setProductVerificationVersion(loadedProductVerificationVersion),
    [loadedProductVerificationVersion]
  );

  useEffect(() => {
    data && setProductVerification(data);
  }, [data]);

  useEffect(() => {
    setError(findProductVerificationError);
  }, [findProductVerificationError]);
  const task = productVerificationVersion?.task || productVerification?.task;

  return (
    <StyledPageTemplate>
      <PageTemplateHeader>
        <HeaderBackButton to={`/portal/products/${productId}/${from}`} />
        <HeaderTitle>
          <h2>{isLoadingProductVerification ? 'Loading...' : title}</h2>
          {productVerification && (
            <div className='status-container'>
              <TaskStatusLabel task={productVerification.task} />
            </div>
          )}
        </HeaderTitle>
        <PageTemplateHeaderActions flex>
          <CartIconBadge
            taskEntity={productVerification}
            onChange={productVerification =>
              setProductVerification(productVerification as ProductVerification)
            }
          />
          {productVerification?.task && (
            <TaskStatusActions
              task={productVerification.task}
              taskEntity={productVerification}
              onChange={(task: Task) => {
                productVerification.task = task;
                setProductVerification({ ...productVerification });
              }}
            />
          )}
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      {productVerification && version && (
        <PageTemplateAlert severity='warning'>
          Viewing previous version {version}
          {productVerificationVersion &&
            ` from ${formatDate(productVerificationVersion.created)}. `}
          <Link to={`./${productVerification.id}`}>
            Click here to view latest version
          </Link>
        </PageTemplateAlert>
      )}
      <PageTemplateDashboard>
        {productVerification && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <VerificationInfoCard {...cardProps} />
                </Grid>
                {!environment.productConfig?.verification?.hideTestSteps && (
                  <Grid item xs={12}>
                    <TestStepCard
                      verificationId={verificationId}
                      onError={cardProps.onError}
                      onUpdate={cardProps.onUpdate}
                      disabled={
                        Boolean(productVerificationVersion) ||
                        isFieldDisabled('testScriptEditor')
                      }
                    />
                  </Grid>
                )}
                {(isJiraConnect || isJiraServer) && (
                  <Grid item xs={12}>
                    <TaskJiraIssueAssignCard
                      task={productVerification.task}
                      entity={{
                        title: productVerification.title,
                        description: productVerification.description
                      }}
                      jiraFieldColumns={
                        environment.productConfig?.verification
                          ?.visibleJiraFields
                      }
                      onChange={taskJiraIssues => {
                        // update productVerification Task jira issues on change
                        productVerification.task.jiraIssues = taskJiraIssues;
                        setProductVerification({ ...productVerification });
                      }}
                    />
                  </Grid>
                )}
                {productVerification.task && (
                  <Grid item xs={12}>
                    <TaskAttachmentCard
                      task={productVerification.task}
                      disabled={Boolean(productVerificationVersion)}
                      attachments={productVerificationVersion?.attachments}
                      onError={cardProps.onError}
                      onChange={handleTaskUpdate}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
              <Grid container spacing={3}>
                {productVerification.task && (
                  <>
                    <Grid item xs={12}>
                      <TaskStatusVersionCard
                        showCart
                        taskEntity={productVerification}
                        renderContent={group =>
                          group.version !==
                            productVerification.task.version && (
                            <HoveredCard
                              title={`Version ${group.version}`}
                              content='View the previous version'
                              actions={
                                <Button
                                  variant='text'
                                  color='primary'
                                  component={Link}
                                  startIcon={<Visibility />}
                                  to={`./${productVerification.id}?ver=${group.version}`}
                                >
                                  View
                                </Button>
                              }
                            />
                          )
                        }
                      />
                    </Grid>
                    {task?.children && task.children.length > 0 && (
                      <Grid item xs={12}>
                        <SubTaskListCard
                          title='Stakeholders'
                          task={task}
                          disabled={
                            Boolean(version) ||
                            isFieldDisabled('assignSubTasks')
                          }
                          onError={cardProps.onError}
                          onChange={handleTaskUpdate}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <ProductsSubSectionComponentCard
                        productSubSection={productVerification}
                        productId={productId}
                        readOnlyComponents={
                          productVerificationVersion &&
                          (productVerificationVersion.components || [])
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <ProductsSubSectionReleaseCard
                        productId={productId}
                        productSubSection={productVerification}
                        disabled={Boolean(productVerificationVersion)}
                        readOnlyRelease={
                          productVerificationVersion &&
                          (productVerificationVersion?.release || null)
                        }
                      />
                    </Grid>
                    {task && (
                      <Grid item xs={12}>
                        <TaskAssignCard task={task} />
                      </Grid>
                    )}
                    {productVerification.task && (
                      <>
                        <Grid item xs={12}>
                          <TaskFileListCard
                            task={productVerification.task}
                            disabled={Boolean(productVerificationVersion)}
                            onError={cardProps.onError}
                            onChange={handleTaskUpdate}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TaskDueDateCard
                            task={productVerification.task}
                            disabled={Boolean(productVerificationVersion)}
                            onError={cardProps.onError}
                            onChange={handleTaskUpdate}
                          />
                        </Grid>
                      </>
                    )}
                  </>
                )}
                <Grid item xs={12}>
                  <VerificationUserNeedReferenceCard
                    {...cardProps}
                    fetchRef={fetchUserNeedsRef}
                  />
                </Grid>
                <Grid item xs={12}>
                  <VerificationDesignInputReferenceCard
                    {...cardProps}
                    fetchRef={fetchDesignInputsRef}
                  />
                </Grid>
                <Grid item xs={12}>
                  <VerificationDesignOutputReferenceCard
                    {...cardProps}
                    onUpdate={() => {
                      fetchUserNeedsRef.current?.();
                      fetchDesignInputsRef.current?.();
                      cardProps.onUpdate?.();
                    }}
                  />
                </Grid>
                {hasRole('Delete') && (
                  <Grid item xs={12}>
                    <DeleteEntityCard
                      title={getProductEntityName('verification')}
                      apiPath={`products/${productId}/verifications`}
                      entity={productVerification}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
      </PageTemplateDashboard>

      <AppSnackbar
        severity='error'
        open={!!error}
        onClose={() => setError(undefined)}
      >
        {error?.message}
      </AppSnackbar>
      <AppSnackbar
        severity='success'
        open={didUpdate}
        onClose={() => didUpdate && setDidUpdate(false)}
      >
        Product {getProductEntityName('verification')} details updated.
      </AppSnackbar>
    </StyledPageTemplate>
  );
};

export default ProductVerificationDetailPage;
